import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSeo from "@/hooks/useSeo";

// Founding-member complimentary access redemption page.
//
// Not linked from public sales pages — Pip shares the URL privately with the
// small group of legacy founding members. They redeem the code once; the page
// auto-creates an account (or upgrades an existing one), grants TMI Live
// access, and signs them in.
export default function RedeemFoundingMember() {
  useSeo({
    title: "Founding Member · Pip Bennett",
    description: "Founding-member complimentary access to The Menopause Invitation — Live.",
    canonical: "https://pipbennett.com/redeem-founding-member",
    robots: "noindex,nofollow",
  });

  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/redeem-founding-member", {
        code: code.trim(),
        email: email.trim(),
        name: name.trim(),
        password,
      });
      if (typeof refreshUser === "function") await refreshUser();
      setDone(true);
      setTimeout(() => navigate("/dashboard", { replace: true }), 2000);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't redeem — please check your code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-redeem-founding-member">
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-gold/20 via-cream-soft to-gold/10 relative">
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-ink max-w-md">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold-dark px-3 py-1 rounded-full text-[0.65rem] tracking-[0.18em] uppercase font-bold mb-6">
              <Sparkles size={11} strokeWidth={2.4} /> Founding Members
            </div>
            <div className="font-serif-display text-5xl leading-[1.05] text-ink">
              Welcome back to <em className="italic text-gold-dark">the work</em>.
            </div>
            <p className="mt-6 text-ink-soft leading-relaxed text-[1.02rem]">
              You were one of the women who said yes to me early — when this work was still finding
              its shape. Your complimentary access to The Menopause Invitation — Live is my way of
              saying thank you, and inviting you to keep walking this with me.
            </p>
            <p className="mt-5 text-ink-soft leading-relaxed text-[1.02rem]">
              Enter the code I shared with you to claim your place.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif-display text-2xl text-ink" data-testid="redeem-logo">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>

          {done ? (
            <div className="mt-12" data-testid="redeem-success">
              <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-gold text-ink mb-6">
                <Check size={20} strokeWidth={1.5} />
              </div>
              <h1 className="font-serif-display text-4xl text-ink leading-tight">You&apos;re in.</h1>
              <p className="mt-5 text-ink-soft leading-relaxed">
                Taking you to your dashboard…
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif-display text-4xl text-ink mt-10 leading-tight">
                Claim your founding-member place.
              </h1>
              <p className="mt-4 text-ink-soft leading-relaxed">
                Complimentary access to <em className="italic">The Menopause Invitation — Live</em>.
              </p>

              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="redeem-form">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Founding-member code</label>
                  <input
                    required
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    autoComplete="off"
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink tracking-[0.15em] uppercase font-semibold focus:outline-none focus:border-gold transition"
                    data-testid="redeem-code"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Your name</label>
                  <input
                    required
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-gold transition"
                    data-testid="redeem-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Email</label>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-gold transition"
                    data-testid="redeem-email"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Choose a password</label>
                  <input
                    required
                    type="password"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-gold transition"
                    data-testid="redeem-password"
                  />
                  <p className="mt-1.5 text-[0.7rem] text-ink-soft/70">
                    At least 8 characters. If you already have an account with this email, we&apos;ll
                    add your founding-member access to it.
                  </p>
                </div>

                {error && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
                    data-testid="redeem-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold hover:bg-gold-dark text-ink rounded-full px-8 py-4 font-bold tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="redeem-submit"
                >
                  {loading ? "Claiming…" : "Claim my place"}
                  {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
                </button>
              </form>

              <p className="mt-8 text-sm text-ink-soft">
                Already have an account?{" "}
                <Link to="/login" className="text-gold-dark hover:text-gold font-medium">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
